import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getCardWalletBalances,
  getWithdrawalBalanceSummary,
  getWithdrawalEligibility,
  initiateWithdrawal,
  previewWithdrawalFee,
  transferCardToMobileMoney,
  type CardWalletBalances,
  type WithdrawalBalanceSummary,
  type WithdrawalFeePreview,
  type WithdrawalInitiateInput,
} from "@/lib/api";
import { formatAmount } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { AlertTriangle, ArrowRight, CheckCircle2, CreditCard, Loader2, Lock, Send, ShieldCheck, Smartphone } from "lucide-react";

type Method = "airtel" | "orange" | "mpesa" | "afrimoney";
type Currency = "CDF" | "USD";
type Source = "mobile" | "card";

const METHOD_LABELS: Record<Method, string> = {
  airtel: "Airtel Money",
  orange: "Orange Money",
  mpesa: "M-Pesa (Vodacom)",
  afrimoney: "Afrimoney",
};

type Step = "form" | "otp" | "success" | "error";

export function WithdrawalInitiatePage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const preselMethod = (search.get("method") ?? "orange") as Method;
  const preselCurrency = (search.get("currency") ?? "CDF") as Currency;
  const preselSource = (search.get("source") ?? "mobile") as Source;

  const [source, setSource] = useState<Source>(preselSource);
  const [step, setStep] = useState<Step>("form");
  const [method, setMethod] = useState<Method>(preselMethod);
  const [currency, setCurrency] = useState<Currency>(preselCurrency);
  const [amountStr, setAmountStr] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState<WithdrawalFeePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [summary, setSummary] = useState<WithdrawalBalanceSummary | null>(null);
  const [cardBalances, setCardBalances] = useState<CardWalletBalances | null>(null);
  const [eligible, setEligible] = useState(false);

  const [otpCode, setOtpCode] = useState("");
  const [otpReference, setOtpReference] = useState<string | null>(null);
  const [otpMessage, setOtpMessage] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successPayload, setSuccessPayload] = useState<{ paydrc_reference?: string; net?: number; reference?: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, e, c] = await Promise.all([
          getWithdrawalBalanceSummary(),
          getWithdrawalEligibility(),
          getCardWalletBalances().catch(() => null),
        ]);
        setSummary(s);
        setEligible(e.has_withdrawal_access);
        setCardBalances(c);
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : "Erreur de chargement");
      }
    })();
  }, []);

  const amount = Number(amountStr);
  const validAmount = Number.isFinite(amount) && amount > 0;

  // Fee preview only relevant on Mobile Money source (card->MM is intra-transfer, no telco fee)
  useEffect(() => {
    let cancelled = false;
    if (source !== "mobile" || !validAmount) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    previewWithdrawalFee({ amount, currency, method })
      .then((p) => { if (!cancelled) setPreview(p); })
      .catch(() => { if (!cancelled) setPreview(null); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [source, amount, currency, method, validAmount]);

  const mmAvailable = useMemo(() => {
    if (!summary) return 0;
    const cur = summary[currency];
    if (!cur) return 0;
    const op = cur.operator_details.find((o) => o.method === method);
    return op ? Number(op.available_for_withdrawal) : 0;
  }, [summary, currency, method]);

  const cardAvailable = useMemo(() => {
    if (!cardBalances) return 0;
    const row = cardBalances.data.find((c) => c.currency === currency);
    return row ? Number(row.balance) : 0;
  }, [cardBalances, currency]);

  const available = source === "mobile" ? mmAvailable : cardAvailable;
  const insufficient = validAmount && (
    source === "mobile"
      ? preview ? preview.total_to_pay > available : amount > available
      : amount > available
  );

  const onSubmitAmount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validAmount || insufficient) return;

    // Card -> MM: no eligibility check (it's an intra-merchant book move)
    if (source === "mobile" && !eligible) return;

    setErrorMsg(null);
    setSubmitting(true);
    try {
      if (source === "mobile") {
        const input: WithdrawalInitiateInput = { amount, currency, method, note: note || undefined };
        const r = await initiateWithdrawal(input);
        if (r.requires_otp && r.reference) {
          setOtpReference(r.reference);
          setOtpMessage(r.message ?? null);
          setStep("otp");
        } else if (r.paydrc_reference || r.status === "Successful") {
          setSuccessPayload({ paydrc_reference: r.paydrc_reference, net: r.net });
          setStep("success");
        } else {
          setErrorMsg(r.message ?? "Réponse serveur inattendue");
          setStep("error");
        }
      } else {
        // Card wallet -> Mobile Money bucket (intra-merchant, no OTP)
        const r = (await transferCardToMobileMoney({ amount, currency, to_method: method, to_action: "deposit", note: note || undefined })) as {
          reference?: string;
          transfer_id?: string;
        };
        setSuccessPayload({ reference: r.reference });
        setStep("success");
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Erreur");
      setStep("error");
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otpCode) || !otpReference) return;
    setErrorMsg(null);
    setSubmitting(true);
    try {
      const r = await initiateWithdrawal({
        amount, currency, method,
        otp_code: otpCode, otp_reference: otpReference, reference: otpReference,
        note: note || undefined,
      });
      if (r.paydrc_reference || r.status === "Successful" || r.status === "success") {
        setSuccessPayload({ paydrc_reference: r.paydrc_reference, net: r.net });
        setStep("success");
      } else {
        setErrorMsg(r.message ?? "Le retrait n'a pas pu être finalisé");
        setStep("error");
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Erreur OTP");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep("form");
    setAmountStr("");
    setNote("");
    setOtpCode("");
    setOtpReference(null);
    setErrorMsg(null);
    setSuccessPayload(null);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader title="Cashout" subtitle="Retrait depuis vos wallets Mobile Money ou depuis votre solde carte virtuelle." />

      {step === "form" && (
        <>
          <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <header className="px-6 py-4 border-b border-gray-100">
              <div className="text-xs font-semibold uppercase tracking-wide text-[#7e8299]">1 · Source</div>
              <h2 className="text-base font-semibold text-[#181c32] mt-0.5">D'où viennent les fonds ?</h2>
            </header>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SourceCard
                title="Mobile Money"
                subtitle="Depuis vos wallets Airtel, Orange, M-Pesa ou Afrimoney"
                Icon={Smartphone}
                selected={source === "mobile"}
                onClick={() => setSource("mobile")}
              />
              <SourceCard
                title="Carte virtuelle"
                subtitle={cardBalances?.total === 0 ? "Aucune carte configurée" : "Depuis votre solde carte (CyberSource)"}
                Icon={CreditCard}
                disabled={cardBalances?.total === 0}
                selected={source === "card"}
                onClick={() => cardBalances?.total !== 0 && setSource("card")}
              />
            </div>
          </section>

          {source === "card" && (
            <div className="rounded border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
              <strong>Transfert interne</strong> — Le solde carte est déplacé vers votre wallet Mobile Money. Aucun code OTP n'est requis.
              Vous pourrez ensuite faire un cashout externe depuis le wallet Mobile Money via ce même écran.
            </div>
          )}

          {source === "mobile" && !eligible && (
            <div className="rounded border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                Service <code>MOBILE_MONEY_WITHDRAWAL</code> non activé. Contactez le support.
              </div>
            </div>
          )}

          <form onSubmit={onSubmitAmount} className="space-y-5 bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-[#7e8299]">2 · {source === "mobile" ? "Retrait" : "Transfert"}</div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#7e8299] mb-1">
                  {source === "mobile" ? "Méthode" : "Wallet cible"}
                </label>
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value as Method)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                >
                  {(Object.keys(METHOD_LABELS) as Method[]).map((m) => (
                    <option key={m} value={m}>{METHOD_LABELS[m]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#7e8299] mb-1">Devise</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as Currency)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="CDF">CDF</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[#7e8299] mb-1">
                Montant · disponible : <span className="font-mono">{formatAmount(Math.round(available * 100), currency)}</span>
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                placeholder="0"
                className="w-full rounded border border-gray-300 px-3 py-3 text-lg font-mono focus:outline-none focus:border-[#181c32]"
              />
              {insufficient && (
                <div className="mt-1 text-xs text-danger-700">Solde insuffisant.</div>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[#7e8299] mb-1">Note (optionnel)</label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={255}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            {source === "mobile" && validAmount && (
              <div className="rounded border border-gray-200 bg-gray-50 p-4 space-y-1.5">
                {previewLoading ? (
                  <div className="text-sm text-[#7e8299]"><Loader2 className="inline animate-spin mr-1" size={12} /> Calcul des frais…</div>
                ) : preview ? (
                  <>
                    <Row label="Montant à recevoir" value={formatAmount(Math.round(amount * 100), currency)} bold />
                    {preview.has_fee ? (
                      <Row label={`Frais ${preview.fee_label}`} value={formatAmount(Math.round(preview.fee_amount * 100), currency)} hint={`+${preview.fee_rate_pct.toFixed(2)} %`} />
                    ) : (
                      <Row label="Frais opérateur" value="—" hint="Aucun frais sur cette méthode" />
                    )}
                    <div className="border-t border-gray-200 mt-2 pt-2">
                      <Row label="Débité de votre wallet" value={formatAmount(Math.round(preview.total_to_pay * 100), currency)} bold />
                    </div>
                  </>
                ) : null}
              </div>
            )}

            {source === "card" && validAmount && (
              <div className="rounded border border-gray-200 bg-gray-50 p-4 space-y-1.5">
                <Row label="Débité de la carte" value={formatAmount(Math.round(amount * 100), currency)} bold />
                <Row label="Crédité sur wallet" value={`${METHOD_LABELS[method]} · deposit`} />
              </div>
            )}

            <button
              type="submit"
              disabled={!validAmount || insufficient || submitting || (source === "mobile" && !eligible)}
              className="w-full rounded bg-[#181c32] hover:bg-[#0f1220] disabled:opacity-50 text-white text-sm font-semibold py-3 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              {submitting ? "Envoi…" : source === "mobile" ? "Continuer" : "Transférer"}
            </button>
          </form>
        </>
      )}

      {step === "otp" && (
        <form onSubmit={onSubmitOtp} className="space-y-5 bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-emerald-100 p-2"><ShieldCheck size={22} className="text-emerald-700" /></div>
            <div>
              <div className="text-base font-semibold text-[#181c32]">Confirmez votre retrait</div>
              <div className="text-sm text-[#7e8299]">{otpMessage ?? "Un code OTP a été envoyé à votre adresse email."}</div>
            </div>
          </div>
          <div className="rounded border border-gray-200 bg-gray-50 p-4 text-sm space-y-1">
            <Row label="Méthode" value={METHOD_LABELS[method]} />
            <Row label="Montant" value={formatAmount(Math.round(amount * 100), currency)} bold />
            <Row label="Référence" value={otpReference ?? "—"} mono />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-[#7e8299] mb-1">
              <Lock size={12} className="inline mr-1" /> Code OTP (6 chiffres)
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              className="w-full rounded border border-gray-300 px-3 py-3 text-xl font-mono tracking-widest text-center focus:outline-none focus:border-[#181c32]"
            />
          </div>
          {errorMsg && <div className="text-sm text-danger-700">{errorMsg}</div>}
          <div className="flex gap-2">
            <button type="button" onClick={reset} className="rounded border border-gray-300 px-4 py-2.5 text-sm text-[#3f4254] hover:bg-gray-50">Annuler</button>
            <button
              type="submit"
              disabled={!/^\d{6}$/.test(otpCode) || submitting}
              className="flex-1 rounded bg-[#181c32] hover:bg-[#0f1220] disabled:opacity-50 text-white text-sm font-semibold py-2.5 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />}
              {submitting ? "Vérification…" : "Confirmer le retrait"}
            </button>
          </div>
        </form>
      )}

      {step === "success" && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 space-y-3">
          <div className="flex items-center gap-3">
            <CheckCircle2 size={28} className="text-emerald-700" />
            <div>
              <div className="text-lg font-bold text-emerald-900">
                {source === "mobile" ? "Retrait initié avec succès" : "Transfert carte → mobile money effectué"}
              </div>
              <div className="text-sm text-emerald-800">
                {source === "mobile" ? "Vérifiez votre historique pour le statut final." : "Le solde est disponible sur votre wallet Mobile Money."}
              </div>
            </div>
          </div>
          {(successPayload?.paydrc_reference || successPayload?.reference) && (
            <div className="rounded border border-emerald-200 bg-white p-3 text-sm">
              <Row label="Référence" value={successPayload?.paydrc_reference ?? successPayload?.reference ?? "—"} mono />
              {successPayload?.net !== undefined && (
                <Row label="Net envoyé" value={formatAmount(Math.round((successPayload.net ?? 0) * 100), currency)} bold />
              )}
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={reset} className="rounded border border-emerald-300 px-4 py-2 text-sm text-emerald-800 hover:bg-emerald-100">Nouveau cashout</button>
            <button onClick={() => navigate("/finance/withdrawal/history")} className="rounded bg-emerald-700 hover:bg-emerald-800 px-4 py-2 text-sm font-semibold text-white">Historique</button>
          </div>
        </div>
      )}

      {step === "error" && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 space-y-3">
          <div className="flex items-center gap-3">
            <AlertTriangle size={24} className="text-red-700" />
            <div className="text-lg font-bold text-red-900">Erreur</div>
          </div>
          <div className="text-sm text-red-800">{errorMsg ?? "Une erreur est survenue."}</div>
          <button onClick={reset} className="rounded border border-red-300 px-4 py-2 text-sm text-red-800 hover:bg-red-100">Recommencer</button>
        </div>
      )}
    </div>
  );
}

function SourceCard({ title, subtitle, Icon, selected, onClick, disabled }: { title: string; subtitle: string; Icon: typeof CreditCard; selected: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`rounded-lg border-2 p-4 text-left transition-colors ${
        disabled ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed" :
        selected ? "border-[#181c32] bg-white" : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`rounded-lg p-2 ${selected ? "bg-[#181c32] text-white" : "bg-gray-100 text-[#3f4254]"}`}>
          <Icon size={18} />
        </div>
        <div>
          <div className="text-sm font-semibold text-[#181c32]">{title}</div>
          <div className="text-xs text-[#7e8299]">{subtitle}</div>
        </div>
      </div>
    </button>
  );
}

function Row({ label, value, bold, hint, mono }: { label: string; value: string; bold?: boolean; hint?: string; mono?: boolean }) {
  return (
    <div className="flex justify-between items-baseline gap-4">
      <div className="text-sm text-[#7e8299]">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} ${bold ? "font-semibold" : ""} text-[#181c32]`}>
        {value}
        {hint && <span className="ml-2 text-xs text-[#7e8299]">{hint}</span>}
      </div>
    </div>
  );
}
